import { useRef, ChangeEvent, useState, useEffect } from "react";
import ReactGA from "react-ga4";
import moment from "moment";
import {
  getBase64,
  isFileSupported,
  cloneFile,
  FileTooBigError,
  HeicConversionError,
  MAX_HEIC_SIZE_LABEL,
} from "../../utils/file";

import check from "../../images/check.svg";
import trash from "../../images/trash.svg";
import camera from "../../images/camera.svg";
import sync from "../../images/sync.svg";
import pdf from "../../images/pdf.png";

type ButtonPictureProps = {
  title: string;
  value: string | undefined;
  onChange: (base64: string, formatted: string) => void;
  required?: boolean;
  isLabelOptionnal?: boolean;
  iconUrl?: string;
};

interface ImageData {
  base64: string;
  formatted: string;
}

function ButtonPicture({
  title,
  onChange,
  value,
  required,
  isLabelOptionnal = true,
  iconUrl,
}: ButtonPictureProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [currentImage, setCurrentImage] = useState<ImageData | null>(
    value ? { base64: value, formatted: "" } : null
  );
  const [isConverting, setIsConverting] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (currentImage) {
      onChange(currentImage.base64, currentImage.formatted);
    } else {
      onChange("", "");
    }
  }, [currentImage]);

  return (
    <div className="button-picture-container">
      {currentImage ? (
        <>
          <div onClick={() => setCurrentImage(null)}>
            <img className="button-picture-icon" alt="trash" src={trash} />
          </div>
          <div className="button-picture-viewer">
            <img
              height={
                currentImage.base64.includes("image/png") ||
                currentImage.base64.includes("image/jpg") ||
                currentImage.base64.includes("image/jpeg")
                  ? ""
                  : "36"
              }
              src={
                currentImage.base64.includes("image/png") ||
                currentImage.base64.includes("image/jpg") ||
                currentImage.base64.includes("image/jpeg")
                  ? currentImage.base64
                  : pdf
              }
              alt={title}
            />
          </div>
          <div onClick={() => inputRef.current?.click()}>
            <img className="button-picture-icon-right" alt="sync" src={sync} />
          </div>
        </>
      ) : null}
      <button
        className="button-picture"
        disabled={isConverting}
        onClick={() => {
          ReactGA.event({
            category: "click_picture",
            action: "open_camera",
            value: 0,
          });
          window.umami?.track("file_picker_opened", { component: "ButtonPicture" });
          inputRef.current?.click();
        }}
      >
        {isConverting ? (
          <span>Conversion en cours...</span>
        ) : (
          <>
            {iconUrl ? (
              <img
                className="button-picture-icon-left"
                alt=""
                src={iconUrl}
              />
            ) : null}
            <span>
              {title}{" "}
              {required
                ? "(Obligatoire)"
                : isLabelOptionnal
                ? "(Facultatif)"
                : ""}
            </span>
            <img alt="camera" src={currentImage ? check : camera} />
          </>
        )}
      </button>
      <input
        style={{ position: "absolute", top: -1000, left: -1000 }}
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/heic,image/heif,.pdf"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files[0]) {
            let file = e.target.files[0];

            window.umami?.track("file_picked", {
              component: "ButtonPicture",
              type: file.type || "unknown",
              size: file.size,
            });

            if (!isFileSupported(file)) {
              window.umami?.track("unsupported_file_format", { type: file.type, name: file.name, component: "ButtonPicture" });
              alert("Format non supporté. Formats acceptés : JPG, PNG, GIF, WebP, HEIC, PDF.");
              return;
            }

            const time = file.lastModified
              ? moment(file.lastModified)
              : moment();

            const formatted = time.format("DD/MM/YYYY HH:mm:ss");

            setIsConverting(true);
            cloneFile(file).then((cloned) => getBase64(cloned))
              .then((result) => {
                ReactGA.event({
                  category: "click_picture",
                  action: "close_camera_true",
                  value: 1,
                });
                window.umami?.track("file_processed", {
                  component: "ButtonPicture",
                  final_type: result.type || "unknown",
                });
                setCurrentImage({ base64: result.base64, formatted });
              })
              .catch((err) => {
                ReactGA.event({
                  category: "click_picture",
                  action: "close_camera_error",
                  value: 3,
                });
                if (err instanceof FileTooBigError) {
                  window.umami?.track("file_error", {
                    component: "ButtonPicture",
                    reason: "too_big",
                    size: file.size,
                  });
                  alert(
                    `Le fichier est trop volumineux. Taille maximale autorisée : ${MAX_HEIC_SIZE_LABEL}.`
                  );
                } else if (err instanceof HeicConversionError) {
                  window.umami?.track("file_error", {
                    component: "ButtonPicture",
                    reason: "heic_failed",
                  });
                  alert(
                    "Impossible de convertir cette image HEIC. Veuillez réessayer ou utiliser un autre format."
                  );
                } else {
                  window.umami?.track("file_error", {
                    component: "ButtonPicture",
                    reason: "unknown",
                    error: String((err as any)?.message || err).substring(0, 200),
                  });
                  alert("Une erreur est survenue lors du traitement du fichier.");
                }
              })
              .finally(() => {
                setIsConverting(false);
                if (inputRef.current) inputRef.current.value = "";
              });
          } else {
            ReactGA.event({
              category: "click_picture",
              action: "close_camera_false",
              value: 2,
            });
            window.umami?.track("file_picker_cancelled", { component: "ButtonPicture" });
          }
        }}
      />
    </div>
  );
}

export default ButtonPicture;
